import React from "react";
import PropTypes from "prop-types";
import { useTranslation, Trans } from "react-i18next";
import { useLocation } from "react-router-dom";

import { WeezHeader, Card, Tab } from "@weezevent/nacre";

import { ATTENDEES_PDF, PAGE_CHECKPOINTS } from "actions/api/exports/actions";
import { useExport } from "actions/hooks/exports/";
import Organization from "models/organization";
import { CONSTANTS } from "reducers/checkpoint";
import SearchBarFilters from "components/checkpoint/searchBarFilters";
import ModalExportPDF from "components/generic/modalExportPDF";
import { GET } from "utils/fetcher";
import useQueryParamsFilters from "utils/useQueryParamsFilters";
import CheckpointsList from "./components/CheckpointsList";
import useCheckpoints from "./hooks/useCheckpoints";
import useChangeCheckpointsStatus from "./hooks/useChangeCheckpointsStatus";
import useDuplicateCheckpoint from "./hooks/useDuplicateCheckpoint";
import ChecklistExport from "./components/ChecklistExport";
import useQueryParams from "../../../utils/useQueryParams";
import { useCheckpointsCapacities } from "../../../utils/rights";
import AdaptiveHeaderButton from "../../../components/adaptive-header-button";
import { PAGE_SIZE_OPTIONS } from "../constants";
import useRouterContext from "../../../context/routerContext";
import useWeezTags from "../../../api/api-hooks/use-weez-tags";
import useAutoRefetchedQuery from "hooks/useAutoRefetchedQuery";
import { useBrowserTabTitle } from "hooks/useBrowserTabTitle";
import { useEventContext } from "hooks/useEventContext";

const filtersDefinitions = {
	status: {
		allowed: [
			CONSTANTS.ARCHIVED,
			CONSTANTS.NOT_ARCHIVED,
			CONSTANTS.ON,
			CONSTANTS.OFF
		],
		default: CONSTANTS.NOT_ARCHIVED
	},
	created: {
		allowed: [CONSTANTS.SIX_MONTHS, CONSTANTS.TWELVE_MONTHS]
	},
	population_tag_id__in: {
		toInternalValue(representation) {
			if (typeof representation === "string") {
				return new Set(
					representation
						.split(",")
						.filter(id => id.trim() !== "")
						.map(id => id.trim())
				);
			}
			return representation ? new Set(representation) : undefined;
		},
		toRepresentation(value) {
			if (value instanceof Set) {
				return Array.from(value || []).join(",");
			}
			if (Array.isArray(value)) {
				return value.join(",");
			}
			if (typeof value === "string") {
				return value
					.split(",")
					.filter(id => id.trim() !== "")
					.join(",");
			}
			return value;
		}
	},

	ordering: {
		allowed: ["name-asc", "name-desc"],
		toInternalValue(representation) {
			if (representation) {
				const [column, direction] = representation
					.map(v => v.toUpperCase())
					.split("-");

				return { column, direction };
			} else {
				return { column: null, direction: null };
			}
		},
		toRepresentation(value) {
			if (value.column && value.direction) {
				return `${value.column}-${value.direction}`.toLowerCase();
			} else {
				return null;
			}
		}
	},
	search: {
		toInternalValue(representation) {
			return representation ?? "";
		}
	},
	pageSize: {
		toInternalValue: representation => {
			const value = parseInt(representation);
			return PAGE_SIZE_OPTIONS.includes(value)
				? value
				: PAGE_SIZE_OPTIONS[0];
		}
	}
};

const CheckpointsView = ({ organization }) => {
	const { t } = useTranslation();
	const organizationId = organization.id();
	const eventId = useEventContext();
	useBrowserTabTitle(
		eventId
			? `O${organizationId} • E${eventId} • ${t(
					"access.titles.checkpoints"
			  )}`
			: `O${organizationId} • ${t("access.titles.checkpoints")}`
	);

	const { path } = useRouterContext();
	const location = useLocation();

	const { filters, setFilter } = useQueryParamsFilters(filtersDefinitions);
	const { pageSize } = filters;
	const setPageSize = React.useCallback(
		newSize => setFilter("pageSize", newSize),
		[setFilter]
	);

	const {
		data,
		loading,
		totalCount,
		loadPage,
		currentPage,
		debouncedDeps,
		refetch
	} = useAutoRefetchedQuery({
		...useCheckpoints(organization.id(), filters, pageSize),
		filter: checklist => checklist.getStatistics().expiredData()
	});

	const { data: weezTagsData } = useWeezTags({
		params: { type: "CHECKLIST" }
	});

	// Checkpoints selected for multiple actions
	const [selectedCheckpointIds, setSelectedCheckpointIds] = React.useState(
		[]
	);

	React.useEffect(() => {
		if (!loading) {
			setSelectedCheckpointIds(checkpoints => {
				return checkpoints.filter(
					checkpoint => !!data.find(v => v.id() === checkpoint)
				);
			});
		}
	}, [data, loading]);

	const changeCheckpointsStatus = useChangeCheckpointsStatus(
		organization.id(),
		filters,
		debouncedDeps
	);

	const duplicateCheckpoint = useDuplicateCheckpoint();

	const { createExport } = useExport();
	const [
		checkpointSelectedForAttendeeListExport,
		setCheckpointSelectedForAttendeeListExport
	] = React.useState();
	async function downloadAttendeesExport(ordering, company_field) {
		// TODO: remove when the access will be free
		// We have to call this route to generate synchonization
		// before to call the export. Otherwise, fields will be empty.
		// This is a stand-by solution before to migrate entirely with Toast.
		// Note: I don't understand what the comment above means and what the problem is (was ?)
		// but the "solution" is to call a butter endpoint
		// so whatever the problem we're trying to solve is
		// we already solved it in butter and can reuse the solution during the export if needed
		await GET(
			`/bo/organizers/${organization.id()}/checkpoints/${checkpointSelectedForAttendeeListExport.id()}/attendees/`
		);

		setCheckpointSelectedForAttendeeListExport(null);

		createExport(
			ATTENDEES_PDF,
			{
				checkpoint_id: checkpointSelectedForAttendeeListExport.id(),
				ordering,
				company_field
			},
			{ page: PAGE_CHECKPOINTS }
		);
	}

	/**
	 * downloadReportExport - create an export for participation or scan report
	 * @type {string} process - The export type (called "process" in gofast talk)
	 */
	const createExportForCheckpoint = React.useCallback(
		(checkpoints, process) => {
			createExport(
				process,
				{
					organizer_id: organization.id(),
					checkpoints_id: checkpoints.map(c => c.id())
				},
				{ page: PAGE_CHECKPOINTS }
			);
		},
		[createExport, organization]
	);

	const toggleOrdering = React.useCallback(
		ordering => {
			if (!ordering) {
				setFilter("ordering", { column: null, direction: null });
			} else {
				setFilter("ordering", ordering);
			}
		},
		[setFilter]
	);

	const renderCheckList = React.useCallback(
		() => (
			<Card style={{ flexGrow: 1, maxWidth: "100%" }}>
				<Card.Content>
					<SearchBarFilters
						filters={filters}
						onChangeFilter={setFilter}
						totalCount={totalCount}
						showPeriodsFilter={false}
						weezTagsData={weezTagsData}
					/>
					<CheckpointsList
						loading={loading}
						organization={organization}
						checkpoints={data}
						changeCheckpointsStatus={changeCheckpointsStatus}
						exportAttendeesList={
							setCheckpointSelectedForAttendeeListExport
						}
						createExport={createExportForCheckpoint}
						duplicateCheckpoint={duplicateCheckpoint}
						ordering={filters.ordering}
						toggleOrdering={toggleOrdering}
						pagination={{
							pageSize,
							totalCount,
							currentPage,
							onPageChange: loadPage
						}}
						selectedCheckpointIds={{
							value: selectedCheckpointIds,
							onChange: setSelectedCheckpointIds
						}}
						pageSize={{
							value: pageSize,
							onChange: setPageSize
						}}
						refetch={refetch}
					/>
				</Card.Content>
			</Card>
		),

		[
			filters,
			setFilter,
			totalCount,
			weezTagsData,
			loading,
			organization,
			data,
			changeCheckpointsStatus,
			createExportForCheckpoint,
			duplicateCheckpoint,
			toggleOrdering,
			pageSize,
			currentPage,
			loadPage,
			selectedCheckpointIds,
			setPageSize,
			refetch
		]
	);

	const { canAdd, canRead } = useCheckpointsCapacities(organization.id());

	const panes = React.useMemo(
		() => [
			{
				menuItem: t("access.checkpoint.tabsLabels.checklist"),
				menuIcon: "qr",
				render: renderCheckList
			},
			{
				menuItem: t("access.checkpoint.tabsLabels.checklistExport"),
				menuIcon: "File-export",
				render: () => (
					<div style={{ flexGrow: 1, maxWidth: "100%" }}>
						<ChecklistExport />
					</div>
				)
			}
		],
		[renderCheckList, t]
	);

	const [activeTab, setActiveTab] = React.useState(0);

	const onChangeTab = React.useCallback(
		tab => {
			const tabIndex = panes.findIndex(v => v.menuItem === tab.menuItem);

			if (tabIndex !== -1) {
				setActiveTab(tabIndex);
			}
		},
		[panes]
	);

	const [params, setParams] = useQueryParams();

	React.useEffect(() => {
		if (params.export) {
			setActiveTab(1);
			setParams({});
		}
	}, [params, setParams]);

	return (
		<div className="checkpoints-view">
			<WeezHeader
				//responsive
				title={t("access.checkpoint.checkpointsUpper")}
				rightComponent={
					canAdd ? (
						<AdaptiveHeaderButton
							label={t("access.checkpoint.newCheckpoint")}
							icon="plus"
							to={`${path}/checkpoints/new${location.search}`}
						/>
					) : (
						[]
					)
				}
			>
				{<Trans i18nKey="access.checkpoint.subtitle" />}
			</WeezHeader>

			{canRead ? (
				<>
					{checkpointSelectedForAttendeeListExport && (
						<ModalExportPDF
							checkpoint={checkpointSelectedForAttendeeListExport}
							open={true}
							onClose={() =>
								setCheckpointSelectedForAttendeeListExport(null)
							}
							downloadExport={downloadAttendeesExport}
						/>
					)}
					<Tab
						activeTab={activeTab}
						onTabSelected={onChangeTab}
						panes={panes}
					/>
				</>
			) : (
				"You have no access to see this page"
			)}
		</div>
	);
};

CheckpointsView.propTypes = {
	organization: PropTypes.instanceOf(Organization).isRequired
};

export default CheckpointsView;
